import { supabase } from '../config/supabase';
import type { NotificationCategory, NotificationSeverity } from './notificationEngine';

export type NotificationThresholdUnit = 'Days' | 'Hours' | 'Matches' | 'Weeks';

export type NotificationSettingDefinition = {
  notificationKey: string;
  displayName: string;
  category: Extract<NotificationCategory, 'Action Required' | 'Alerts & Reminders'>;
  defaultEnabled: boolean;
  defaultSeverity: NotificationSeverity;
  defaultThresholdValue: number | null;
  defaultThresholdUnit: NotificationThresholdUnit | null;
  minThresholdValue?: number;
  maxThresholdValue?: number;
  description: string;
};

export type EffectiveNotificationSetting = {
  notificationKey: string;
  enabled: boolean;
  severity: NotificationSeverity;
  thresholdValue: number | null;
  thresholdUnit: NotificationThresholdUnit | null;
};

export type NotificationSettingRow = {
  id: string;
  workspace_id: string;
  notification_key: string;
  enabled: boolean;
  severity: NotificationSeverity;
  threshold_value: number | null;
  threshold_unit: NotificationThresholdUnit | null;
};

export const notificationSettingDefinitions: NotificationSettingDefinition[] = [
  {
    notificationKey: 'official_result_imported', displayName: 'Official Results Imported', category: 'Alerts & Reminders',
    defaultEnabled: true, defaultSeverity: 'Informational', defaultThresholdValue: null, defaultThresholdUnit: null,
    description: 'Informational confirmation when official scores are imported automatically or match already saved results. No approval is required.',
  },
  {
    notificationKey: 'participation_review_needed',
    displayName: 'Participation Review Needed',
    category: 'Action Required',
    defaultEnabled: true,
    defaultSeverity: 'Warning',
    defaultThresholdValue: null,
    defaultThresholdUnit: null,
    description: 'Review match participation that requires a captain decision.',
  },
  {
    notificationKey: 'official_provider_score_disagreement', displayName: 'Provider Score Differs from Captain Score', category: 'Action Required',
    defaultEnabled: true, defaultSeverity: 'Warning', defaultThresholdValue: 3, defaultThresholdUnit: 'Days', minThresholdValue: 1, maxThresholdValue: 30,
    description: 'Review a USTA score that differs from captain-entered scores. USTA remains the official source; the number of days is the default snooze period.',
  },
  {
    notificationKey: 'season_provider_time_change', displayName: 'Provider Match Time Change', category: 'Action Required',
    defaultEnabled: true, defaultSeverity: 'Warning', defaultThresholdValue: 3, defaultThresholdUnit: 'Days', minThresholdValue: 1, maxThresholdValue: 30,
    description: 'Review provider match-time changes. The number of days is the default snooze period.',
  },
  {
    notificationKey: 'season_provider_new_match', displayName: 'New Provider Match', category: 'Action Required',
    defaultEnabled: true, defaultSeverity: 'Warning', defaultThresholdValue: 3, defaultThresholdUnit: 'Days', minThresholdValue: 1, maxThresholdValue: 30,
    description: 'Review newly discovered provider matches before importing them as Draft. The number of days is the default snooze period.',
  },
  {
    notificationKey: 'season_provider_player_change', displayName: 'Provider Player Change', category: 'Action Required',
    defaultEnabled: true, defaultSeverity: 'Warning', defaultThresholdValue: 3, defaultThresholdUnit: 'Days', minThresholdValue: 1, maxThresholdValue: 30,
    description: 'Review new provider players and changed provider names or registration. Canonical player details are never overwritten. The number of days is the default snooze period.',
  },
  {
    notificationKey: 'assigned_player_availability_changed',
    displayName: 'Assigned Player Availability Changed',
    category: 'Action Required',
    defaultEnabled: true,
    defaultSeverity: 'Critical',
    defaultThresholdValue: null,
    defaultThresholdUnit: null,
    description: 'Shows when a player assigned to a lineup changes from Available to Maybe or Unavailable, regardless of whether the change was made by the player, GroupMe, or a captain.',
  },
  {
    notificationKey: 'lineup_not_started',
    displayName: 'Lineup Not Started',
    category: 'Action Required',
    defaultEnabled: true,
    defaultSeverity: 'Warning',
    defaultThresholdValue: 7,
    defaultThresholdUnit: 'Days',
    minThresholdValue: 1,
    maxThresholdValue: 30,
    description: 'Shows before a match when the lineup has not been started.',
  },
  {
    notificationKey: 'lineup_not_published',
    displayName: 'Incomplete Lineup',
    category: 'Action Required',
    defaultEnabled: true,
    defaultSeverity: 'Warning',
    defaultThresholdValue: 7,
    defaultThresholdUnit: 'Days',
    minThresholdValue: 1,
    maxThresholdValue: 30,
    description: 'Shows before a match when the lineup is not published.',
  },
  {
    notificationKey: 'match_scores_required',
    displayName: 'Scores Missing',
    category: 'Action Required',
    defaultEnabled: true,
    defaultSeverity: 'Critical',
    defaultThresholdValue: 24,
    defaultThresholdUnit: 'Hours',
    minThresholdValue: 1,
    maxThresholdValue: 168,
    description: 'Shows after a scheduled match when no match scores have been captured.',
  },
  {
    notificationKey: 'match_scores_partial',
    displayName: 'Partial Scores Saved',
    category: 'Action Required',
    defaultEnabled: true,
    defaultSeverity: 'Warning',
    defaultThresholdValue: null,
    defaultThresholdUnit: null,
    description: 'Shows when score entry has started but is still saved as draft.',
  },
  {
    notificationKey: 'player_registration_needed',
    displayName: 'Registration Incomplete',
    category: 'Action Required',
    defaultEnabled: true,
    defaultSeverity: 'Informational',
    defaultThresholdValue: null,
    defaultThresholdUnit: null,
    description: 'Shows when an active roster player has not been verified as registered.',
  },
  {
    notificationKey: 'missing_availability',
    displayName: 'Missing Availability Reminder',
    category: 'Alerts & Reminders',
    defaultEnabled: true,
    defaultSeverity: 'Warning',
    defaultThresholdValue: 3,
    defaultThresholdUnit: 'Days',
    minThresholdValue: 1,
    maxThresholdValue: 30,
    description: 'Shows when a match is approaching and players still have unknown availability.',
  },
  {
    notificationKey: 'upcoming_birthday',
    displayName: 'Birthday Reminder',
    category: 'Alerts & Reminders',
    defaultEnabled: true,
    defaultSeverity: 'Informational',
    defaultThresholdValue: 14,
    defaultThresholdUnit: 'Days',
    minThresholdValue: 1,
    maxThresholdValue: 365,
    description: 'Shows upcoming player birthdays in Command Center.',
  },
  {
    notificationKey: 'players_below_target',
    displayName: 'Player Below Participation Target',
    category: 'Alerts & Reminders',
    defaultEnabled: true,
    defaultSeverity: 'Informational',
    defaultThresholdValue: 2,
    defaultThresholdUnit: 'Matches',
    minThresholdValue: 1,
    maxThresholdValue: 20,
    description: 'Shows players projected below their adjusted participation target.',
  },
  {
    notificationKey: 'player_not_played_recently',
    displayName: "Player Hasn't Played Recently",
    category: 'Alerts & Reminders',
    defaultEnabled: true,
    defaultSeverity: 'Informational',
    defaultThresholdValue: 3,
    defaultThresholdUnit: 'Weeks',
    minThresholdValue: 1,
    maxThresholdValue: 52,
    description: 'Shows active roster players who have not completed a match recently.',
  },
  {
    notificationKey: 'tennisrecord_edr_sync_due',
    displayName: 'TennisRecord EDR Sync Due',
    category: 'Alerts & Reminders',
    defaultEnabled: true,
    defaultSeverity: 'Warning',
    defaultThresholdValue: 1,
    defaultThresholdUnit: 'Weeks',
    minThresholdValue: 1,
    maxThresholdValue: 52,
    description: 'Reminds you to run the browser-based TennisRecord EDR sync for players who have not been refreshed recently.',
  },
  {
    notificationKey: 'home_match_welcome_message',
    displayName: 'Upcoming Match Reminder',
    category: 'Alerts & Reminders',
    defaultEnabled: true,
    defaultSeverity: 'Informational',
    defaultThresholdValue: 7,
    defaultThresholdUnit: 'Days',
    minThresholdValue: 1,
    maxThresholdValue: 30,
    description: 'Reminds captains to send the home match welcome message.',
  },
  {
    notificationKey: 'team_event_details_confirmation',
    displayName: 'Confirm Team Event Time & Location',
    category: 'Alerts & Reminders',
    defaultEnabled: true,
    defaultSeverity: 'Informational',
    defaultThresholdValue: 7,
    defaultThresholdUnit: 'Days',
    minThresholdValue: 1,
    maxThresholdValue: 30,
    description: 'Reminds captains to confirm the time and location of upcoming practices, socials, team meetings, and other non-match events.',
  },
  {
    notificationKey: 'tcd_scoresheet_print_reminder',
    displayName: 'TCD Scoresheet Print Reminder',
    category: 'Alerts & Reminders',
    defaultEnabled: true,
    defaultSeverity: 'Informational',
    defaultThresholdValue: 2,
    defaultThresholdUnit: 'Days',
    minThresholdValue: 1,
    maxThresholdValue: 30,
    description: 'Reminds you to print the TCD scoresheet before upcoming TCD matches.',
  },
  {
    notificationKey: 'away_match_follow_up',
    displayName: 'Away Match Follow-Up',
    category: 'Alerts & Reminders',
    defaultEnabled: true,
    defaultSeverity: 'Informational',
    defaultThresholdValue: 4,
    defaultThresholdUnit: 'Days',
    minThresholdValue: 1,
    maxThresholdValue: 30,
    description: 'Reminds captains to check for away match details from the opposing captain.',
  },
];

const definitionsByKey = new Map(notificationSettingDefinitions.map((definition) => [definition.notificationKey, definition]));

function defaultSetting(definition: NotificationSettingDefinition): EffectiveNotificationSetting {
  return {
    notificationKey: definition.notificationKey,
    enabled: definition.defaultEnabled,
    severity: definition.defaultSeverity,
    thresholdValue: definition.defaultThresholdValue,
    thresholdUnit: definition.defaultThresholdUnit,
  };
}

export function defaultNotificationSetting(notificationKey: string): EffectiveNotificationSetting {
  const definition = definitionsByKey.get(notificationKey);
  if (!definition) {
    return {
      notificationKey,
      enabled: true,
      severity: 'Informational',
      thresholdValue: null,
      thresholdUnit: null,
    };
  }
  return defaultSetting(definition);
}

export function validateNotificationThreshold(definition: NotificationSettingDefinition, value: number | null): string | null {
  if (!definition.defaultThresholdUnit) return null;
  if (value === null || !Number.isInteger(value)) return `${definition.displayName} threshold must be a whole number.`;
  if (definition.minThresholdValue !== undefined && value < definition.minThresholdValue) return `${definition.displayName} threshold must be at least ${definition.minThresholdValue}.`;
  if (definition.maxThresholdValue !== undefined && value > definition.maxThresholdValue) return `${definition.displayName} threshold must be ${definition.maxThresholdValue} or less.`;
  return null;
}

export type NotificationSettingsByWorkspace = Map<string, Map<string, EffectiveNotificationSetting>>;

export async function loadNotificationSettingsByWorkspace(workspaceIds: string[]): Promise<NotificationSettingsByWorkspace> {
  const uniqueWorkspaceIds = [...new Set(workspaceIds.filter(Boolean))];
  const settingsByWorkspace: NotificationSettingsByWorkspace = new Map();
  uniqueWorkspaceIds.forEach((workspaceId) => {
    settingsByWorkspace.set(workspaceId, new Map(notificationSettingDefinitions.map((definition) => [definition.notificationKey, defaultSetting(definition)])));
  });
  if (uniqueWorkspaceIds.length === 0) return settingsByWorkspace;

  const { data, error } = await supabase
    .from('notification_settings')
    .select('id, workspace_id, notification_key, enabled, severity, threshold_value, threshold_unit')
    .in('workspace_id', uniqueWorkspaceIds);

  if (error) {
    if (error.code === '42P01' || error.code === '42703' || error.message.includes('notification_settings')) return settingsByWorkspace;
    throw new Error(error.message);
  }

  ((data || []) as unknown as NotificationSettingRow[]).forEach((row) => {
    const definition = definitionsByKey.get(row.notification_key);
    const workspaceMap = settingsByWorkspace.get(row.workspace_id);
    if (!definition || !workspaceMap) return;
    workspaceMap.set(row.notification_key, {
      notificationKey: row.notification_key,
      enabled: row.enabled,
      severity: row.severity || definition.defaultSeverity,
      thresholdValue: definition.defaultThresholdUnit ? row.threshold_value ?? definition.defaultThresholdValue : null,
      thresholdUnit: definition.defaultThresholdUnit,
    });
  });

  return settingsByWorkspace;
}

export function getNotificationSetting(settingsByWorkspace: NotificationSettingsByWorkspace, workspaceId: string | null | undefined, notificationKey: string): EffectiveNotificationSetting {
  const definition = definitionsByKey.get(notificationKey);
  const fallback = definition ? defaultSetting(definition) : defaultNotificationSetting(notificationKey);
  if (!workspaceId) return fallback;
  return settingsByWorkspace.get(workspaceId)?.get(notificationKey) || fallback;
}
